import { useMemo, useState } from "react";
import { DocumentUpload } from "V2.0/HomePages/Assistants/MessagingSettings/DocumentCollectionSettings/DocumentUpload";
import { Button } from "V2.0/common/button";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { createVoiceClone } from "../../service";
import { showErrorToast } from "V2.0/utils";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "V2.0/common/dialog";
import InputComponent from "V2.0/components/InputComponent";
import useCloneAudioState from "./State/useCloneAudioState";
import { message } from "antd";

export function CloneAudio(props) {
  const { cloneAudioState, updateName, updateDescription, updateFiles } = props;

  const Files = useMemo(
    function () {
      if (cloneAudioState.files) {
        const file = cloneAudioState.files.file;
        const audioUrl = URL.createObjectURL(file);

        return (
          <div className="flex flex-col space-y-2 border rounded-lg p-4">
            <div className="flex items-center justify-between gap-x-2">
              <p className="text-sm font-medium">{file.name}</p>
              <div>
                <Button
                  plain
                  onClick={() => {
                    updateFiles(null);
                  }}
                >
                  <XMarkIcon />
                </Button>
              </div>
            </div>
            <div className="w-full">
              <audio
                controls
                className=""
              >
                <source src={audioUrl} />
              </audio>
            </div>
          </div>
        );
      }

      return (
        <DocumentUpload
          _allowedFileTypes={["audio/ogg", "audio/mp3", "audio/wav"]}
          multiple={false}
          onChange={(file) => updateFiles(file)}
        />
      );
    },
    [cloneAudioState.files, updateFiles]
  );

  return (
    <div className="space-y-4">
      <InputComponent
        type="text"
        label="Name *"
        invalid={Boolean(cloneAudioState.errors.name)}
        error={cloneAudioState.errors.name}
        onChange={(e) => updateName(e.target.value)}
        value={cloneAudioState.name}
      />
      <InputComponent
        type="textArea"
        label="Description"
        style={{ height: "100px", fontSize: "13px" }}
        value={cloneAudioState.description}
        onChange={(e) => updateDescription(e.target.value)}
      />
      <div className="w-full space-y-4">
        <p className="text-sm font-medium">Audio File *</p>
        <div>{Files}</div>
        {cloneAudioState.errors.files && (
          <p className="text-sm text-red-500">
            * {cloneAudioState.errors.files}
          </p>
        )}
      </div>
    </div>
  );
}

export default function CloneAudioDialog(props) {
  const { onClose, fetchVoices, ...restProps } = props;

  const {
    cloneAudioState,
    updateName,
    updateDescription,
    updateFiles,
    validateCloneAudioState,
  } = useCloneAudioState();

  const [loading, setLoading] = useState(false);

  async function handleClone() {
    const { isValid } = validateCloneAudioState(cloneAudioState);

    if (!isValid) {
      return;
    }

    const formData = new FormData();
    formData.append("files", cloneAudioState.files.file);
    formData.append("name", cloneAudioState.name);
    formData.append("description", cloneAudioState.description);

    const lifecycleCbs = {
      init: () => {
        message.loading("Cloning Voice");
        setLoading(true);
      },
      success: () => {
        message.destroy();
        message.success("Voice Cloned");
        setLoading(false);
        fetchVoices();
        onClose();
      },
      error: (e) => {
        message.destroy();
        setLoading(false);
        showErrorToast(e, "Failed to clone voice");
      },
    };

    lifecycleCbs.init();

    console.log({ formData });
    try {
      const response = await createVoiceClone({ payload: formData });

      if (response.status === 200) {
        console.log({ response: response.data });
        lifecycleCbs.success();
      }
    } catch (e) {
      lifecycleCbs.error(e);
    }
  }

  const isCloneDisabled =
    loading || Object.values(cloneAudioState.errors).some((error) => error);

  return (
    <Dialog
      open={true}
      className="2xl"
      onClose={() => {}}
    >
      <DialogTitle>Voice Design - Clone Audio</DialogTitle>
      <DialogBody>
        <CloneAudio
          {...restProps}
          cloneAudioState={cloneAudioState}
          updateName={updateName}
          updateDescription={updateDescription}
          updateFiles={updateFiles}
          validateCloneAudioState={validateCloneAudioState}
        />
      </DialogBody>
      <DialogActions>
        <Button
          onClick={onClose}
          outline
        >
          Cancel
        </Button>
        <Button
          loading={loading}
          disabled={isCloneDisabled}
          onClick={handleClone}
        >
          Clone
        </Button>
      </DialogActions>
    </Dialog>
  );
}
